// @ts-check

const BUTTON = {
  primary: 0,
  secondary: 1,
  notes: 2,
  hint: 3,
  digitPrevious: 4,
  digitNext: 5,
  undo: 6,
  redo: 7,
  pause: 9,
  up: 12,
  down: 13,
  left: 14,
  right: 15,
};

const STICK_THRESHOLD = 0.55;
const REPEAT_DELAY = 320;
const REPEAT_INTERVAL = 115;

export class GamepadController {
  constructor(handlers, environment = {}) {
    this.handlers = handlers;
    this.selectedDigit = 1;
    this.connectedIndex = null;
    this.connectedName = "";
    this.controls = new Map();
    this.running = false;
    this.frame = null;
    this.eventTarget = environment.eventTarget ?? window;
    this.getGamepads =
      environment.getGamepads ?? (() => navigator.getGamepads?.() ?? []);
    this.requestFrame =
      environment.requestFrame ??
      ((callback) => requestAnimationFrame(callback));
    this.cancelFrame =
      environment.cancelFrame ?? ((frame) => cancelAnimationFrame(frame));
    this.onConnected = (event) => this.connect(event.gamepad);
    this.onDisconnected = (event) => this.disconnect(event.gamepad.index);
  }

  start() {
    if (this.running || !("getGamepads" in navigator)) return false;
    this.running = true;
    this.eventTarget.addEventListener("gamepadconnected", this.onConnected);
    this.eventTarget.addEventListener(
      "gamepaddisconnected",
      this.onDisconnected,
    );
    const tick = (time) => {
      if (!this.running) return;
      const gamepads = [...this.getGamepads()].filter(Boolean);
      const gamepad =
        gamepads.find((entry) => entry.index === this.connectedIndex) ??
        gamepads.find((entry) => entry.mapping === "standard") ??
        gamepads[0];
      if (gamepad) this.process(gamepad, time);
      else if (this.connectedIndex !== null)
        this.disconnect(this.connectedIndex);
      this.frame = this.requestFrame(tick);
    };
    this.frame = this.requestFrame(tick);
    return true;
  }

  stop() {
    this.running = false;
    if (this.frame !== null) this.cancelFrame(this.frame);
    this.eventTarget.removeEventListener("gamepadconnected", this.onConnected);
    this.eventTarget.removeEventListener(
      "gamepaddisconnected",
      this.onDisconnected,
    );
  }

  connect(gamepad) {
    this.connectedIndex = gamepad.index;
    this.connectedName = friendlyName(gamepad.id);
    this.handlers.status?.({
      connected: true,
      name: this.connectedName,
      digit: this.selectedDigit,
    });
  }

  disconnect(index) {
    if (this.connectedIndex !== index) return;
    this.connectedIndex = null;
    this.connectedName = "";
    this.controls.clear();
    this.handlers.status?.({
      connected: false,
      name: "",
      digit: this.selectedDigit,
    });
  }

  process(gamepad, time = performance.now()) {
    if (this.connectedIndex !== gamepad.index) this.connect(gamepad);
    const button = (name) => pressed(gamepad.buttons[BUTTON[name]]);
    const horizontal = Number(gamepad.axes?.[0] ?? 0);
    const vertical = Number(gamepad.axes?.[1] ?? 0);
    const actions = [
      {
        name: "up",
        down: button("up") || vertical < -STICK_THRESHOLD,
        repeat: true,
        action: () => this.handlers.move?.(-1, 0),
      },
      {
        name: "down",
        down: button("down") || vertical > STICK_THRESHOLD,
        repeat: true,
        action: () => this.handlers.move?.(1, 0),
      },
      {
        name: "left",
        down: button("left") || horizontal < -STICK_THRESHOLD,
        repeat: true,
        action: () => this.handlers.move?.(0, -1),
      },
      {
        name: "right",
        down: button("right") || horizontal > STICK_THRESHOLD,
        repeat: true,
        action: () => this.handlers.move?.(0, 1),
      },
      {
        name: "digitPrevious",
        down: button("digitPrevious"),
        repeat: false,
        action: () => this.chooseDigit(-1),
      },
      {
        name: "digitNext",
        down: button("digitNext"),
        repeat: false,
        action: () => this.chooseDigit(1),
      },
      {
        name: "primary",
        down: button("primary"),
        repeat: false,
        action: () => this.handlers.primary?.(this.selectedDigit),
      },
      {
        name: "secondary",
        down: button("secondary"),
        repeat: false,
        action: () => this.handlers.secondary?.(),
      },
      {
        name: "notes",
        down: button("notes"),
        repeat: false,
        action: () => this.handlers.notes?.(),
      },
      {
        name: "hint",
        down: button("hint"),
        repeat: false,
        action: () => this.handlers.hint?.(),
      },
      {
        name: "undo",
        down: button("undo"),
        repeat: false,
        action: () => this.handlers.undo?.(),
      },
      {
        name: "redo",
        down: button("redo"),
        repeat: false,
        action: () => this.handlers.redo?.(),
      },
      {
        name: "pause",
        down: button("pause"),
        repeat: false,
        action: () => this.handlers.pause?.(),
      },
    ];
    for (const { name, down, repeat, action } of actions) {
      if (this.consume(name, down, time, repeat)) {
        document.body.dataset.inputModality = "controller";
        action();
      }
    }
  }

  consume(name, down, time, repeat) {
    const state = this.controls.get(name) ?? { down: false, next: 0 };
    if (!down) {
      this.controls.set(name, { down: false, next: 0 });
      return false;
    }
    if (!state.down) {
      this.controls.set(name, { down: true, next: time + REPEAT_DELAY });
      return true;
    }
    if (repeat && time >= state.next) {
      this.controls.set(name, { down: true, next: time + REPEAT_INTERVAL });
      return true;
    }
    return false;
  }

  chooseDigit(change) {
    this.selectedDigit = ((this.selectedDigit - 1 + change + 9) % 9) + 1;
    this.handlers.status?.({
      connected: true,
      name: this.connectedName,
      digit: this.selectedDigit,
    });
  }
}

function pressed(button) {
  return Boolean(button?.pressed || Number(button?.value ?? 0) > 0.5);
}

function friendlyName(id) {
  return String(id || "Standard controller")
    .replace(/\s*\([^)]*\)\s*/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 48);
}
